import { useState, type PointerEvent } from 'react'
import type { RatioComponentSeries } from '../../../../data/ServerApiClient'
import { formatRatioValue } from '../cfoFormat'
import './DualAxisTrendLine.css'

// Two-line, dual-axis trend of a ratio's two raw inputs (e.g. Total Liabilities
// vs Total Assets). Each series gets its own Y scale so you can see, at a glance,
// whether the numerator is climbing or the denominator falling. Colorblind-safe:
// the two lines differ by hue AND dash pattern, and the legend carries the
// first→last change for each input so the "which moved" answer is explicit.

type Props = {
  series: [RatioComponentSeries, RatioComponentSeries]
  ariaLabel: string
  width?: number
  height?: number
  maxPoints?: number
  className?: string
}

const SERIES_COLORS = ['var(--brand)', 'var(--check-acc)'] as const

const num = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

const monthLabel = (period: string | null | undefined): string => {
  if (!period) return ''
  const parsed = new Date(`${String(period).slice(0, 10)}T00:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return String(period).slice(0, 7)
  return parsed.toLocaleDateString(undefined, { month: 'short', timeZone: 'UTC' })
}

const pctChange = (first: number, last: number): string => {
  if (!Number.isFinite(first) || first === 0) return '—'
  const pct = (last - first) / Math.abs(first)
  const sign = pct > 0 ? '+' : pct < 0 ? '−' : ''
  return `${sign}${Math.abs(pct * 100).toFixed(0)}%`
}

export function DualAxisTrendLine({ series, ariaLabel, width = 880, height = 200, maxPoints = 12, className }: Props) {
  const [tooltipIndex, setTooltipIndex] = useState<number | null>(null)
  const tails = series.map((s) => (s.trend || []).slice(-maxPoints))
  const len = Math.max(tails[0].length, tails[1].length)
  if (len < 2) return null

  const padL = 8
  const padR = 8
  const padTop = 26
  const padBottom = 26
  const plotW = width - padL - padR
  const plotH = height - padTop - padBottom
  const toX = (i: number) => padL + (i / Math.max(len - 1, 1)) * plotW

  const scaleFor = (vals: number[]) => {
    const finite = vals.filter((v) => Number.isFinite(v))
    const lo = finite.length ? Math.min(...finite) : 0
    const hi = finite.length ? Math.max(...finite) : 1
    const pad = (hi - lo || Math.abs(hi) || 1) * 0.14
    const min = lo - pad
    const span = hi + pad - min || 1
    return (v: number) => padTop + (1 - (v - min) / span) * plotH
  }

  const lines = tails.map((tail, si) => {
    const values = tail.map((p) => num(p.value) ?? Number.NaN)
    const toY = scaleFor(values)
    const pts = values
      .map((v, i) => (Number.isFinite(v) ? `${toX(i).toFixed(1)},${toY(v).toFixed(1)}` : null))
      .filter(Boolean)
      .join(' ')
    const lastIdx = values.reduce((acc, v, i) => (Number.isFinite(v) ? i : acc), -1)
    const lastPt = lastIdx >= 0 ? { x: toX(lastIdx), y: toY(values[lastIdx]) } : null
    const firstVal = values.find((v) => Number.isFinite(v))
    const lastVal = lastIdx >= 0 ? values[lastIdx] : null
    const pointAt = (idx: number) => {
      const value = values[idx]
      return Number.isFinite(value) ? { x: toX(idx), y: toY(value), value } : null
    }
    return { si, pts, lastPt, firstVal: firstVal ?? null, lastVal, pointAt, color: SERIES_COLORS[si] }
  })

  const periods = tails[0].length >= tails[1].length ? tails[0] : tails[1]
  const xTicks = [0, Math.floor((len - 1) / 2), len - 1]
  const activeIndex = tooltipIndex == null ? null : Math.min(Math.max(tooltipIndex, 0), len - 1)
  const activePeriod = activeIndex == null ? null : periods[activeIndex]?.period
  const activeX = activeIndex == null ? 50 : (toX(activeIndex) / width) * 100
  const tooltipSide = activeX > 72 ? 'left' : activeX < 28 ? 'right' : 'center'
  const activeYValues = activeIndex == null
    ? []
    : lines
      .map((ln) => ln.pointAt(activeIndex)?.y)
      .filter((value): value is number => value != null && Number.isFinite(value))
  const activeY = activeYValues.length
    ? (activeYValues.reduce((sum, value) => sum + value, 0) / activeYValues.length / height) * 100
    : 50
  const activeRows = activeIndex == null
    ? []
    : series.map((s, si) => {
      const point = lines[si].pointAt(activeIndex)
      const previous = lines[si].pointAt(activeIndex - 1)
      const change = point && previous ? point.value - previous.value : null
      return {
        id: s.id,
        label: s.label,
        color: SERIES_COLORS[si],
        value: point ? formatRatioValue(point.value, s.unit) : '—',
        change: change == null ? null : `${change >= 0 ? '+' : ''}${formatRatioValue(change, s.unit)}`,
      }
    })
  const updateTooltipFromPointer = (event: PointerEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    const x = ((event.clientX - rect.left) / Math.max(rect.width, 1)) * width
    const idx = Math.round(((x - padL) / Math.max(plotW, 1)) * Math.max(len - 1, 1))
    setTooltipIndex(Math.min(Math.max(idx, 0), len - 1))
  }

  return (
    <div className={`cfo-dual-trend${className ? ` ${className}` : ''}`}>
      <div className="cfo-dual-trend-legend">
        {series.map((s, si) => {
          const ln = lines[si]
          const first = ln.firstVal
          const last = ln.lastVal
          return (
            <div className="cfo-dual-trend-legrow" key={s.id}>
              <span className="cfo-dual-trend-swatch" style={{ background: SERIES_COLORS[si] }} aria-hidden="true" />
              <span className="cfo-dual-trend-leglabel">{s.label}</span>
              <span className="cfo-dual-trend-legval">
                {first != null ? formatRatioValue(first, s.unit) : '—'} → {last != null ? formatRatioValue(last, s.unit) : '—'}
                {first != null && last != null ? <em> ({pctChange(first, last)})</em> : null}
              </span>
            </div>
          )
        })}
      </div>
      <div
        className="cfo-dual-trend-chart"
        tabIndex={0}
        aria-label={`${ariaLabel}. Hover or focus for point details.`}
        onPointerEnter={updateTooltipFromPointer}
        onPointerMove={updateTooltipFromPointer}
        onPointerLeave={() => setTooltipIndex(null)}
        onFocus={() => setTooltipIndex(len - 1)}
        onBlur={() => setTooltipIndex(null)}
      >
        <svg
          className="cfo-dual-trend-svg"
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label={ariaLabel}
        >
          <line x1={padL} y1={padTop + plotH} x2={padL + plotW} y2={padTop + plotH} stroke="var(--grid)" strokeWidth={1.5} />
          {activeIndex != null ? (
            <line
              x1={toX(activeIndex)}
              y1={padTop}
              x2={toX(activeIndex)}
              y2={padTop + plotH}
              stroke="var(--axis)"
              strokeWidth={1}
              strokeDasharray="4 5"
              opacity={0.45}
            />
          ) : null}
          {lines.map((ln) => (
            <g key={ln.si}>
              <polyline
                points={ln.pts}
                fill="none"
                stroke={ln.color}
                strokeWidth={ln.si === 0 ? 3 : 2.4}
                strokeDasharray={ln.si === 0 ? undefined : '5 4'}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {(() => {
                const activePoint = activeIndex == null ? null : ln.pointAt(activeIndex)
                if (activePoint) {
                  return (
                    <>
                      <circle cx={activePoint.x} cy={activePoint.y} r={7} fill="var(--surface)" />
                      <circle cx={activePoint.x} cy={activePoint.y} r={4.4} fill={ln.color} />
                    </>
                  )
                }
                if (!ln.lastPt) return null
                return (
                  <>
                    <circle cx={ln.lastPt.x} cy={ln.lastPt.y} r={6.5} fill="var(--surface)" />
                    <circle cx={ln.lastPt.x} cy={ln.lastPt.y} r={4} fill={ln.color} />
                  </>
                )
              })()}
            </g>
          ))}
          {xTicks.map((i) => (
            <text
              key={i}
              x={toX(i)}
              y={height - 8}
              textAnchor={i === 0 ? 'start' : i === len - 1 ? 'end' : 'middle'}
              style={{ font: '600 11px var(--font-ui)', fill: 'var(--muted)' }}
            >
              {monthLabel(periods[i]?.period)}
            </text>
          ))}
        </svg>
        {activeIndex != null ? (
          <div
            className={`cfo-dual-trend-tooltip ${tooltipSide}`}
            role="tooltip"
            style={{
              left: `${Math.min(92, Math.max(8, activeX))}%`,
              top: `${Math.min(82, Math.max(16, activeY))}%`,
            }}
          >
            <div className="cfo-dual-trend-tooltip-period">{monthLabel(activePeriod) || 'Period'}</div>
            {activeRows.map((row) => (
              <div key={row.id} className="cfo-dual-trend-tooltip-row">
                <span className="cfo-dual-trend-tooltip-swatch" style={{ background: row.color }} aria-hidden="true" />
                <span className="cfo-dual-trend-tooltip-label">{row.label}</span>
                <strong>{row.value}</strong>
                {row.change ? <em>{row.change} vs prior</em> : null}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default DualAxisTrendLine
